<template>
    <v-text-field
        :id="id"
        ref="field"
        :value="inputValue"
        :type="type"
        :autofocus="autofocus"
        :error-messages="errorMessages"
        :error="invalid"
        :class="{ 'is-invalid': invalid }"
        class="form-control"
        :name="id"
        :maxlength="maxlength"
        :disabled="disabled"
        outlined
        v-bind="$attrs"
        v-on="listeners"
        @focus="onfocus"
        @blur="onblur"
    >
        <template #label>
            <slot name="label"><span>{{ label }}</span></slot> <sup :class="{invisible: !required}">*</sup>
        </template>

        <template #message="messageProps">
            <slot name="message" :message="messageProps.message" />
        </template>
    </v-text-field>

    <!--
    WARNING: For reasons unknown, masking will stop working if v-text-field has a body
    -->
</template>

<script>
    import Imask from "imask"
    import FieldMixin from "@/components/form/FieldMixin"
    import { whenMouseUp } from "@/util/dom"
    import { convertSmartChars } from "@/util/string"

    export default {
        mixins: [FieldMixin],

        inheritAttrs: false,

        props: {
            autofocus: {
                type: Boolean,
                default: false,
            },

            inputmode: {
                type: String,
            },

            mask: {
                type: [Object, String, RegExp],
                default: () => /.*/,
            },

            validateOnBlur: {
                type: Boolean,
                default: true,
            },

            size: {
                type: String,
            },

            type: {
                type: String,
                default: "text",
            },

            minlength: {
                type: [Number, String],
                default: null,
            },

            maxlength: {
                type: [Number, String],
                default: null,
            },

            convertSmartCharacters: {
                type: Boolean,
                default: null
            },

            trim: {
                type: Boolean,
                default: null
            }
        },

        data() {
            return {
                focused: false,
                overrideMaskPlaceholderOnFocus: true,
            }
        },

        computed: {
            shouldConvertSmartChars() {
                if (this.convertSmartCharacters != null) return this.convertSmartCharacters
                return !!this.mask
            },

            shouldTrim() {
                if (this.trim != null) return this.trim
                return !!this.mask
            },

            inputValue() {
                if (!this.masked) {
                    return this.sanitize(this.value)
                }

                return this.sanitize(this.masked.value)
            },

            listeners() {
                const { input, focus, blur, ...listeners } = this.$listeners
                return listeners
            },

            maskConfig() {
                if (typeof this.mask === "object" && !(this.mask instanceof RegExp) && !Array.isArray(this.mask)) {
                    return this.mask
                }

                return {
                    mask: this.mask,
                    lazy: true, // change to false to make placeholder visible
                }
            },
        },

        watch: {
            inputmode(value) {
                if (value) {
                    this.element.setAttribute("inputmode", value)
                } else {
                    this.element.removeAttribute("inputmode")
                }
            },

            value(value) {
                if (value !== this.masked.unmaskedValue) {
                    this.masked.unmaskedValue = this.sanitize(value || "")
                    this.element.dispatchEvent(new Event("input"))
                }
            },

            mask() {
                setTimeout(() => {
                    this.reloadMask()
                    if (this.focused) {
                        this.element.dispatchEvent(new Event("focus"))
                    }
                })
            },
        },

        beforeDestroy() {
            this.masked.destroy()
        },

        mounted() {
            this.element = this.$refs.field.$el.querySelector("input")

            if (this.inputmode) {
                this.element.setAttribute("inputmode", this.inputmode)
            }

            this.masked = Imask(this.element, this.maskConfig)
            this.masked.on("accept", this.update)

            // need to do this in order for masking to initally kick-in
            this.element.dispatchEvent(new Event("input"))
        },

        methods: {
            applyValidations(target) {
                FieldMixin.methods.applyValidations.call(this, target)

                if (this.minlength !== null) {
                    this.applyValidator(target, "minLength", parseInt(this.minlength))
                }

                if (this.maxlength !== null) {
                    this.applyValidator(target, "maxLength", parseInt(this.maxlength))
                }
            },

            update() {
                const unmaskedValue = this.masked.unmaskedValue
                const newUnmaskedValue = this.sanitize(unmaskedValue, true)

                if (unmaskedValue !== newUnmaskedValue) {
                    this.masked.unmaskedValue = newUnmaskedValue
                }

                this.$emit("input", this.masked.unmaskedValue)
                setTimeout(() => this.reloadMask())
            },

            onfocus(event) {
                this.focused = true
                this.$emit("focus", event)
                if (this.vcform) this.vcform.emitFieldEvent("focus", this, event)
            },

            onblur(event) {
                this.focused = false
                let isNormalized = this.element.value === this.masked.value

                if (!isNormalized) {
                    this.element.value = this.masked.value
                    this.element.dispatchEvent(new Event("input"))
                    setTimeout(() => this.reloadMask())
                }

                const unmasked = this.masked.unmaskedValue
                const sanitized = this.sanitize(unmasked)

                if (unmasked !== sanitized) {
                    this.$emit("input", sanitized)
                }

                this.$emit("blur", event)
                if (this.vcform) this.vcform.emitFieldEvent("blur", this, event)

                if (this.validateOnBlur) {
                    whenMouseUp().then(() => {
                        setTimeout(() => this.$emit("validate"))
                    })
                }
            },

            reloadMask(config) {
                this.masked.updateOptions(config || this.maskConfig)
                this.masked.updateControl()
            },

            sanitize(value, updating) {
                if (!value) return value
                if (!updating && this.shouldTrim) value = value.trim()
                if (this.shouldConvertSmartChars) value = convertSmartChars(value)
                return value
            }
        },
    }
</script>
